import { Checkbox, Divider, FormGroup, Radio, RadioGroup, Spacer } from '@ksyos/design-system';
import { useState } from 'react';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import type { IOrthOV1DiagnosticReportCustomControlDataOrthoptischeDiagnose } from '../../../../../common/validation/customControls/ophthalmology';
import type { IOrthOV1DiagnosticReportAddEditCustomControlFieldsProps } from './OrthOV1DiagnosticReportAddEditCustomControlFields';
import { ConclusieDiagnoseOption } from '../../../../../common/enums/conclusieDiagnoseOption';
import { orthODiagnosticReportCustomControlOrthoptischeDiagnoseLabels } from '../../../../../common/validation/customControls/ophthalmology';
import { nullDiagnosis } from '../../../../../common/validation/diagnosis';
import { translateKey } from '../../../../translate';
import { HeaderDivider } from '../../../elements/common';
import { FormContextProvider } from '../../../elements/formContext';
import { InputContainer } from '../../../elements/forms/InputContainer';
import { FormValidationMessage } from '../../../elements/FormValidationMessage';
import { ProcessItemHeader } from '../../episodes/ProcessItemHeader';
import { Icd10Selections } from '../utils/v1/Icd10/Icd10Selections';
import { orthoptischeDiagnosisInitialValues } from './OrthOV1DiagnosticReportAddEditCustomControlFields';

export function OrthOV1DiagnosticReportAddEditCustomControlDataConclusie(
    props: IOrthOV1DiagnosticReportAddEditCustomControlFieldsProps
) {
    const {
        diagnosticReport,
        errors,
        service: { specialismCode },
        customControlData,
        customControlData: { orthoptische_diagnose, heb_je_een_orthoptische_diagnose },
        handleChange,
        handleChangeCustomControl,
        validateOneCustomControl,
        validateOne,
    } = props;
    const [isAndersOrthoptischeDiagnoseChecked, setIsAndersOrthoptischeDiagnoseChecked] = useState(
        diagnosticReport.diagnosis.diagnosisConsiderationIcd10 !== null ||
            diagnosticReport.diagnosis.considerationThesaurusTermId !== null
    );

    return (
        <>
            <FormContextProvider
                value={{
                    placeholderGroupName: 'diagnosticReport',
                    item: orthoptische_diagnose,
                    errors: errors,
                    onChange: async (key, value) => {
                        if (!isOrthoptischeDiagnoseSet()) {
                            await clearDiagnosis();
                        }
                        await handleChangeOrthoptischeDiagnose(key, value);
                    },
                    onDoValidate: async () => await validateOneCustomControl('orthoptische_diagnose'),
                }}
            >
                <ProcessItemHeader
                    isFirstItem={false}
                    isItemOpen={true}
                    process={undefined}
                    title={translateKey('common.conclusion')}
                />

                <HeaderDivider text="Diagnose" />

                <FormGroup
                    hasErrors={errors.has('customControlData.heb_je_een_orthoptische_diagnose')}
                    isRequired={true}
                    label="Heb je een orthoptische diagnose?"
                >
                    <RadioGroup
                        error={getValidationErrorTranslation(
                            errors.get('customControlData.heb_je_een_orthoptische_diagnose')
                        )}
                        layout="row"
                        name="heb_je_een_orthoptische_diagnose"
                        onChange={(e) => {
                            handleChangeDiagnosisConclusion(e.target.value);
                        }}
                        value={heb_je_een_orthoptische_diagnose ?? ''}
                    >
                        <Radio
                            label="Ja"
                            value={ConclusieDiagnoseOption.ja}
                        />
                        <Radio
                            label="Nee"
                            value={ConclusieDiagnoseOption.nog_geen_diagnose}
                        />
                        <Radio
                            label="Geen afwijkingen"
                            value={ConclusieDiagnoseOption.geen_afwijkingen}
                        />
                    </RadioGroup>
                </FormGroup>

                {heb_je_een_orthoptische_diagnose === ConclusieDiagnoseOption.ja && (
                    <FormGroup
                        hasErrors={errors.has('diagnosticReport.customControlData.orthoptische_diagnose')}
                        isRequired={true}
                        label="Orthoptische diagnose"
                    >
                        <div className="row">
                            <div
                                className="col-sm-12"
                                style={{ minWidth: 240 }}
                            >
                                <Spacer spacing="small">
                                    <Checkbox
                                        checked={
                                            customControlData.orthoptische_diagnose.refractiestoornis_hypermetropie
                                        }
                                        label={
                                            // eslint-disable-next-line @stylistic/max-len
                                            orthODiagnosticReportCustomControlOrthoptischeDiagnoseLabels.refractiestoornis_hypermetropie
                                        }
                                        onChange={async (event) => {
                                            await handleChangeOrthoptischeDiagnose(
                                                'refractiestoornis_hypermetropie',
                                                event.target.checked
                                            );
                                        }}
                                    />
                                    <Checkbox
                                        checked={
                                            customControlData.orthoptische_diagnose.refractiestoornis_anisometropie
                                        }
                                        label={
                                            // eslint-disable-next-line @stylistic/max-len
                                            orthODiagnosticReportCustomControlOrthoptischeDiagnoseLabels.refractiestoornis_anisometropie
                                        }
                                        onChange={async (event) => {
                                            await handleChangeOrthoptischeDiagnose(
                                                'refractiestoornis_anisometropie',
                                                event.target.checked
                                            );
                                        }}
                                    />
                                    <Checkbox
                                        checked={customControlData.orthoptische_diagnose.refractiestoornis_myopie}
                                        label={
                                            // eslint-disable-next-line @stylistic/max-len
                                            orthODiagnosticReportCustomControlOrthoptischeDiagnoseLabels.refractiestoornis_myopie
                                        }
                                        onChange={async (event) => {
                                            await handleChangeOrthoptischeDiagnose(
                                                'refractiestoornis_myopie',
                                                event.target.checked
                                            );
                                        }}
                                    />
                                    <Checkbox
                                        checked={customControlData.orthoptische_diagnose.refractiestoornis_astigmatisme}
                                        label={
                                            // eslint-disable-next-line @stylistic/max-len
                                            orthODiagnosticReportCustomControlOrthoptischeDiagnoseLabels.refractiestoornis_astigmatisme
                                        }
                                        onChange={async (event) => {
                                            await handleChangeOrthoptischeDiagnose(
                                                'refractiestoornis_astigmatisme',
                                                event.target.checked
                                            );
                                        }}
                                    />
                                </Spacer>
                            </div>
                            <div
                                className="col-sm-12"
                                style={{ minWidth: 240 }}
                            >
                                <Spacer spacing="small">
                                    <Checkbox
                                        checked={
                                            customControlData.orthoptische_diagnose
                                                .insufficientie_en_overmatige_convergentie
                                        }
                                        label={
                                            // eslint-disable-next-line @stylistic/max-len
                                            orthODiagnosticReportCustomControlOrthoptischeDiagnoseLabels.insufficientie_en_overmatige_convergentie
                                        }
                                        onChange={async (event) => {
                                            await handleChangeOrthoptischeDiagnose(
                                                'insufficientie_en_overmatige_convergentie',
                                                event.target.checked
                                            );
                                        }}
                                    />
                                    <Checkbox
                                        checked={customControlData.orthoptische_diagnose.strabismus}
                                        label={orthODiagnosticReportCustomControlOrthoptischeDiagnoseLabels.strabismus}
                                        onChange={async (event) => {
                                            await handleChangeOrthoptischeDiagnose('strabismus', event.target.checked);
                                        }}
                                    />
                                    <Checkbox
                                        checked={customControlData.orthoptische_diagnose.amblyopie}
                                        label={orthODiagnosticReportCustomControlOrthoptischeDiagnoseLabels.amblyopie}
                                        onChange={async (event) => {
                                            await handleChangeOrthoptischeDiagnose('amblyopie', event.target.checked);
                                        }}
                                    />
                                </Spacer>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'start' }}>
                            <Checkbox
                                checked={isAndersOrthoptischeDiagnoseChecked}
                                onChange={async (value) => {
                                    setIsAndersOrthoptischeDiagnoseChecked(!isAndersOrthoptischeDiagnoseChecked);
                                    if (isAndersOrthoptischeDiagnoseChecked) {
                                        await clearDiagnosis();
                                    }
                                    validateOne('diagnosis');
                                    validateOneCustomControl('orthoptische_diagnose');
                                }}
                            />
                            <InputContainer>
                                <Icd10Selections
                                    bold={true}
                                    diagnosis={diagnosticReport.diagnosis}
                                    errors={errors}
                                    hideWorkingDiagnosisLabel={true}
                                    isWorkingDiagnosis={false}
                                    onChange={async (diagnosis) => {
                                        setIsAndersOrthoptischeDiagnoseChecked(
                                            diagnosis.diagnosisConsiderationIcd10 !== null ||
                                                diagnosis.considerationThesaurusTermId !== null
                                        );
                                        await handleChange('diagnosis', diagnosis);
                                    }}
                                    onDoValidate={async () => {
                                        await validateOne('diagnosis');
                                        await validateOneCustomControl('orthoptische_diagnose');
                                    }}
                                    specialismCode={specialismCode || ''}
                                />
                            </InputContainer>
                        </div>
                        <FormValidationMessage error={errors.get('customControlData.orthoptische_diagnose')} />
                    </FormGroup>
                )}
                <Divider />
            </FormContextProvider>
        </>
    );

    function isOrthoptischeDiagnoseSet() {
        if (customControlData && orthoptische_diagnose) {
            const diagnosisOptions = [
                'refractiestoornis',
                'insufficientie_en_overmatige_convergentie',
                'strabismus',
                'amblyopie',
            ];

            return (
                Object.entries(orthoptische_diagnose).find(([key, value]) => {
                    return diagnosisOptions.includes(key) && !!value;
                }) !== undefined || diagnosticReport.diagnosis
            );
        }

        return false;
    }

    async function clearDiagnosis() {
        await handleChange('diagnosis', { ...nullDiagnosis });
    }

    async function handleChangeDiagnosisConclusion(value: string) {
        await handleChangeCustomControl({ heb_je_een_orthoptische_diagnose: value });
        validateOneCustomControl('heb_je_een_orthoptische_diagnose');
        if (value !== ConclusieDiagnoseOption.ja) {
            handleChangeCustomControl({
                orthoptische_diagnose: { ...orthoptischeDiagnosisInitialValues },
            });
            clearDiagnosis();
            validateOneCustomControl('orthoptische_diagnose');
        }
    }

    async function handleChangeOrthoptischeDiagnose<
        K extends keyof IOrthOV1DiagnosticReportCustomControlDataOrthoptischeDiagnose,
    >(name: K, value: IOrthOV1DiagnosticReportCustomControlDataOrthoptischeDiagnose[K]) {
        if (!isOrthoptischeDiagnoseSet()) {
            await clearDiagnosis();
        }
        await handleChangeCustomControl({
            orthoptische_diagnose: {
                ...orthoptische_diagnose,
                [name]: value,
            },
        });

        validateOneCustomControl('orthoptische_diagnose');
    }
}
